import React, { useState } from 'react';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { HelpCircle } from 'lucide-react';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { ClipItem } from './ClipItem';

export interface ProjectClip {
  id: string;
  sourceFileId: string;
  position: number;
  prompt: string;
  referenceText?: string; // Optional reference text to display separately from prompt
  duration: number;
  name: string;
  thumbnailUrl?: string;
  fileType?: 'video' | 'audio' | 'image' | 'srt' | 'text';
  analysisData?: {
    transcript?: any;
    scenes?: any[];
    background?: string;
  };
  layerType?: 'main' | 'background' | 'audio' | 'mask';
  linkedPairId?: string;
  linkedMaskId?: string; // ID of linked luma matte mask clip
  isLinked?: boolean;
  inTime?: number; // Start time in source file (in seconds)
  outTime?: number; // End time in source file (in seconds)
  volume?: number; // Audio volume 0.0 to 1.0 (default 1.0)
  // URL fields for render
  s3Url?: string; // Direct S3 URL for the source file
  url?: string; // Alternative URL field
  s3Key?: string; // S3 key for refreshing URLs
  // Multi-track overlay support
  track?: number; // 0 = main track, 1+ = overlay tracks
  overlayPosition?: 'center' | 'topLeft' | 'topRight' | 'bottomLeft' | 'bottomRight';
  overlayScale?: number; // 0.1 to 1.0 (default 1.0 for full size overlay)
  stackedOn?: string; // ID of the clip this overlays
  isOverlay?: boolean; // Quick check for overlay status
  // Quick Render explicit fields (not parsed from prompt)
  titleText?: string; // Text overlay content
  titlePosition?: 'top-left' | 'top-center' | 'top-right' | 'center-left' | 'center' | 'center-right' | 'bottom-left' | 'bottom-center' | 'bottom-right'; // Position of text overlay
  titleFont?: string; // Font family for title overlay (default: Montserrat)
  titleColor?: string; // Text color for title overlay (hex, default: #ffffff)
  chromaKey?: {
    enabled: boolean;
    threshold: number;
    halo: number;
  };
  transitionOut?: 'none' | 'fade' | 'fadeSlow' | 'fadeFast' | 'wipeLeft' | 'wipeRight' | 'slideLeft' | 'slideRight' | 'slideUp' | 'slideDown';
  // Motion effects (Ken Burns style)
  effect?: 'none' | 'zoomIn' | 'zoomOut' | 'slideLeft' | 'slideRight' | 'slideUp' | 'slideDown' | 'zoomInSlow' | 'zoomOutSlow';
  // Image-specific fields for Quick Render
  imageMode?: 'fullframe' | 'overlay'; // Full frame clip or overlay on video
  imagePosition?: 'top-left' | 'top-center' | 'top-right' | 'center-left' | 'center' | 'center-right' | 'bottom-left' | 'bottom-center' | 'bottom-right';
  imageDuration?: number; // Duration in seconds for image clips
  imageScale?: number; // Scale for overlay images (0.1 to 0.5, default 0.25)
  // Video effects - Shotstack filters (export-level only)
  filter?: 'none' | 'boost' | 'contrast' | 'muted' | 'darken' | 'lighten' | 'greyscale' | 'negative';
  // Captions
  showCaptions?: boolean; // Whether to burn in captions from transcript
  captionStyle?: 'default' | 'outline' | 'box' | 'minimal';
  captionFont?: string; // Font family for captions (default: Open Sans)
  captionBackground?: string; // Background preset: 'dark', 'light', 'transparent'
  captionFontColor?: string; // Font color (hex, default: #ffffff)
  captionStrokeColor?: string; // Stroke/outline color (hex, default: #000000)
}

interface ProjectFile {
  id: string;
  analysisData?: {
    transcript?: {
      words?: any[];
      segments?: any[];
      text?: string;
      full_text?: string;
    };
  };
  [key: string]: any;
}

interface TimelineSequenceProps {
  clips: ProjectClip[];
  files?: ProjectFile[];  // For looking up transcript data
  analysisResults?: {[key: string]: any};  // For looking up transcript from analysis results
  onClipDrop: (fileId: string) => void;
  onClipRemove: (clipId: string) => void;
  onClipReorder: (clipId: string, newPosition: number) => void;
  onPromptChange: (clipId: string, prompt: string) => void;
  onTimeChange?: (clipId: string, inTime: number | undefined, outTime: number | undefined) => void;
  onPreviewClip?: (clipId: string) => void;
  onTitleChange?: (clipId: string, titleText: string | undefined, titlePosition: 'top-left' | 'top-center' | 'top-right' | 'center-left' | 'center' | 'center-right' | 'bottom-left' | 'bottom-center' | 'bottom-right' | undefined, titleFont?: string, titleColor?: string) => void;
  onVolumeChange?: (clipId: string, volume: number) => void;
  onTransitionChange?: (clipId: string, transitionOut: 'none' | 'fade' | 'fadeSlow' | 'fadeFast' | 'wipeLeft' | 'wipeRight' | 'slideLeft' | 'slideRight' | 'slideUp' | 'slideDown') => void;
  onEffectChange?: (clipId: string, effect: 'none' | 'zoomIn' | 'zoomOut' | 'slideLeft' | 'slideRight' | 'slideUp' | 'slideDown' | 'zoomInSlow' | 'zoomOutSlow') => void;
  onImageSettingsChange?: (clipId: string, imageMode: 'fullframe' | 'overlay', imagePosition: 'top-left' | 'top-center' | 'top-right' | 'center-left' | 'center' | 'center-right' | 'bottom-left' | 'bottom-center' | 'bottom-right' | undefined, imageDuration: number, imageScale: number) => void;
  onCaptionsChange?: (clipId: string, showCaptions: boolean, captionStyle: 'default' | 'outline' | 'box' | 'minimal', captionFont?: string, captionBackground?: string, captionFontColor?: string, captionStrokeColor?: string) => void;
  onReferenceTextChange?: (clipId: string, referenceText: string) => void;
  onLayerTypeChange?: (clipId: string, layerType: 'main' | 'background') => void;
  // Multi-track overlay support
  onStackClip?: (draggedClipId: string, targetClipId: string) => void;
  onUnstackClip?: (clipId: string) => void;
  onOverlaySettingsChange?: (clipId: string, position: 'center' | 'topLeft' | 'topRight' | 'bottomLeft' | 'bottomRight', scale: number) => void;
  renderMode?: 'direct' | 'lambda';
}

export const TimelineSequence: React.FC<TimelineSequenceProps> = ({
  clips,
  files,
  analysisResults,
  onClipDrop,
  onClipRemove,
  onClipReorder,
  onPromptChange,
  onTimeChange,
  onPreviewClip,
  onTitleChange,
  onVolumeChange,
  onTransitionChange,
  onEffectChange,
  onImageSettingsChange,
  onCaptionsChange,
  onReferenceTextChange,
  onLayerTypeChange,
  onStackClip,
  onUnstackClip,
  onOverlaySettingsChange,
  renderMode,
}) => {
  const [draggedOverPosition, setDraggedOverPosition] = useState<number | null>(null);
  const [isDraggingOver, setIsDraggingOver] = useState(false);

  // Filter out mask clips (luma mattes shown as badge on their linked video)
  // Only in Quick Render mode (direct)
  const visibleClips = renderMode === 'direct'
    ? clips.filter(clip => clip.layerType !== 'mask')
    : clips;

  // Get main track clips (non-overlay clips)
  const mainTrackClips = visibleClips.filter(clip => !clip.isOverlay);

  // Get overlays for a specific base clip
  const getOverlaysForClip = (clipId: string): ProjectClip[] => {
    return visibleClips.filter(clip => clip.isOverlay && clip.stackedOn === clipId);
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'copy';
    setIsDraggingOver(true);
  };

  const handleDragLeave = () => {
    setIsDraggingOver(false);
    setDraggedOverPosition(null);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    const fileId = e.dataTransfer.getData('fileId');
    if (fileId) {
      onClipDrop(fileId);
    }
    setIsDraggingOver(false);
    setDraggedOverPosition(null);
  };

  return (
    <Card className="border-border">
      <CardHeader className="pb-3">
        <CardTitle className="text-lg font-semibold flex items-center gap-2">
          Timeline / Sequence
          <Tooltip>
            <TooltipTrigger asChild>
              <button type="button" className="text-muted-foreground hover:text-foreground transition-colors">
                <HelpCircle className="h-4 w-4" />
              </button>
            </TooltipTrigger>
            <TooltipContent side="right" className="max-w-xs">
              <p>Arrange your clips here. Drag to reorder, click to select and edit properties like trim, titles, and transitions.</p>
            </TooltipContent>
          </Tooltip>
        </CardTitle>
      </CardHeader>

      <CardContent>
        <div
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
          className={`min-h-[200px] border-2 border-dashed rounded-lg p-4 transition-colors ${
            isDraggingOver
              ? 'border-primary bg-primary/5'
              : 'border-border bg-muted/20'
          }`}
        >
          {visibleClips.length > 0 ? (
            <div className="flex gap-4 overflow-x-auto pb-4">
              {mainTrackClips.map((clip, index) => (
                <ClipItem
                  key={clip.id}
                  clip={clip}
                  sourceFile={files?.find(f => f.id === clip.sourceFileId)}
                  analysisResults={analysisResults}
                  onRemove={() => onClipRemove(clip.id)}
                  onPromptChange={(prompt) => onPromptChange(clip.id, prompt)}
                  onReorder={(newPosition) => onClipReorder(clip.id, newPosition)}
                  onTimeChange={onTimeChange ? (inTime, outTime) => onTimeChange(clip.id, inTime, outTime) : undefined}
                  onOverlayTimeChange={onTimeChange}
                  onPreviewClip={onPreviewClip}
                  onTitleChange={onTitleChange ? (text, pos, font, color) => onTitleChange(clip.id, text, pos, font, color) : undefined}
                  onVolumeChange={onVolumeChange ? (vol) => onVolumeChange(clip.id, vol) : undefined}
                  onTransitionChange={onTransitionChange ? (trans) => onTransitionChange(clip.id, trans) : undefined}
                  onEffectChange={onEffectChange ? (effect) => onEffectChange(clip.id, effect) : undefined}
                  onImageSettingsChange={onImageSettingsChange ? (mode, pos, dur, scale) => onImageSettingsChange(clip.id, mode, pos, dur, scale) : undefined}
                  onCaptionsChange={onCaptionsChange ? (show, style, font, bg, fontColor, strokeColor) => onCaptionsChange(clip.id, show, style, font, bg, fontColor, strokeColor) : undefined}
                  onReferenceTextChange={onReferenceTextChange ? (text) => onReferenceTextChange(clip.id, text) : undefined}
                  onLayerTypeChange={onLayerTypeChange ? (layerType) => onLayerTypeChange(clip.id, layerType) : undefined}
                  onStackClip={onStackClip}
                  onUnstackClip={onUnstackClip}
                  onOverlaySettingsChange={onOverlaySettingsChange}
                  position={index}
                  totalClips={mainTrackClips.length}
                  renderMode={renderMode}
                  allClips={clips}
                  stackedOverlays={getOverlaysForClip(clip.id)}
                />
              ))}
            </div>
          ) : (
            <div className="h-full flex flex-col items-center justify-center text-center py-12">
              <div className="space-y-2">
                <p className="text-sm font-medium text-muted-foreground">
                  Timeline is empty
                </p>
                <p className="text-xs text-muted-foreground max-w-md">
                  Drag files from the Project Bin above to add them to your sequence.
                  You can arrange clips, add prompts, and build your video timeline.
                </p>
              </div>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
};
